/* eslint-disable */
import React, { useState, useMemo } from 'react';

const generateAnonLabel = () => 'DEV-' + Math.random().toString(36).slice(2, 6).toUpperCase();

/**
 * CloseShiftSheet — reconciliation UI for closing a shift.
 * For each open allocation: choose Return / Carry over / Flag.
 */
export default function CloseShiftSheet({ st, setSt, shiftId, account, audit, onBack, onSuccess }) {
  const shift = (st.shifts || []).find(s => s.id === shiftId);
  const openAllocs = useMemo(() =>
    (st.allocations || []).filter(a => a.shiftId === shiftId && a.status === 'out'),
    [st.allocations, shiftId]
  );

  // Find carry-over target shift: same location, planned/active, within 24h of this shift's end
  const carryTargetShift = useMemo(() => {
    if (!shift) return null;
    const endMs = new Date(shift.endAt).getTime();
    return (st.shifts || [])
      .filter(s =>
        s.id !== shiftId &&
        s.locationId === shift.locationId &&
        (s.status === 'planned' || s.status === 'active') &&
        Math.abs(new Date(s.startAt).getTime() - endMs) <= 24 * 3600000
      )
      .sort((a, b) => Math.abs(new Date(a.startAt).getTime() - endMs) - Math.abs(new Date(b.startAt).getTime() - endMs))
      [0] || null;
  }, [st.shifts, shiftId, shift]);

  // decisions: { [allocId]: 'return' | 'carry' | 'flag' }
  const [decisions, setDecisions] = useState(() => {
    const d = {};
    openAllocs.forEach(a => { d[a.id] = 'return'; });
    return d;
  });

  if (!shift) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Shift not found</div>;

  // If no open allocations, close directly
  if (openAllocs.length === 0) {
    const handleCloseEmpty = () => {
      const now = new Date().toISOString();
      setSt(prev => ({
        ...prev,
        shifts: prev.shifts.map(s => s.id === shiftId ? { ...s, status: 'closed', closedAt: now } : s),
      }));
      audit('shift_closed', { shiftId, title: shift.title, decisions: [] });
      if (onSuccess) onSuccess();
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 20, alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-default)', marginBottom: 8 }}>All devices returned</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>No open allocations — shift can close cleanly.</div>
        <button onClick={handleCloseEmpty} style={{ padding: '13px 32px', borderRadius: 10, border: 'none', background: '#ff530d', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Close shift</button>
        <button onClick={onBack} style={{ marginTop: 10, padding: '10px 20px', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
      </div>
    );
  }

  const setDecision = (allocId, action) => setDecisions(prev => ({ ...prev, [allocId]: action }));

  const counts = { return: 0, carry: 0, flag: 0 };
  Object.values(decisions).forEach(d => { counts[d] = (counts[d] || 0) + 1; });
  const resultStatus = counts.flag > 0 ? 'closed_with_open_items' : 'closed';

  const fmtDuration = (outAt) => {
    if (!outAt) return '—';
    const ms = Date.now() - new Date(outAt).getTime();
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  };

  const handleConfirm = () => {
    const now = new Date().toISOString();
    const newAllocs = [];

    setSt(prev => {
      let updatedAllocs = [...prev.allocations];
      let updatedDevices = [...prev.devices];

      openAllocs.forEach(alloc => {
        const action = decisions[alloc.id];
        if (action === 'return') {
          updatedAllocs = updatedAllocs.map(a => a.id === alloc.id ? { ...a, status: 'returned', inAt: now } : a);
          updatedDevices = updatedDevices.map(d => d.id === alloc.deviceId ? { ...d, availability: 'available' } : d);
        } else if (action === 'carry' && carryTargetShift) {
          const carryAllocId = 'alloc-carry-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
          updatedAllocs = updatedAllocs.map(a => a.id === alloc.id ? { ...a, status: 'carried_over', inAt: now } : a);
          newAllocs.push({
            id: carryAllocId, shiftId: carryTargetShift.id, deviceId: alloc.deviceId,
            role: alloc.role, operativeName: alloc.operativeName, operativePhone: alloc.operativePhone,
            outAt: carryTargetShift.startAt || now, inAt: null, status: 'out',
            notes: 'Carried from Shift ' + shift.shiftNumber,
            swappedFromAllocationId: null, swappedToAllocationId: null,
            carriedFromAllocationId: alloc.id,
            anonDeviceLabel: carryTargetShift.anonymousMode ? generateAnonLabel() : null,
            flaggedByUserId: null, flaggedAt: null, recoveredAt: null,
            createdByUserId: account?.username || 'unknown', createdAt: now,
          });
          // Device stays deployed
        } else if (action === 'flag') {
          updatedAllocs = updatedAllocs.map(a => a.id === alloc.id ? { ...a, status: 'flagged_missing', flaggedByUserId: account?.username || 'unknown', flaggedAt: now } : a);
          updatedDevices = updatedDevices.map(d => d.id === alloc.deviceId ? { ...d, availability: 'lost' } : d);
        }
      });

      return {
        ...prev,
        allocations: [...updatedAllocs, ...newAllocs],
        devices: updatedDevices,
        shifts: prev.shifts.map(s => s.id === shiftId ? { ...s, status: resultStatus, closedAt: now } : s),
      };
    });

    audit('shift_closed', { shiftId, title: shift.title, decisions: Object.entries(decisions).map(([id, a]) => ({ allocationId: id, action: a })) });
    if (onSuccess) onSuccess();
  };

  const actionBtn = (allocId, action, label, selectedStyle) => {
    const isSelected = decisions[allocId] === action;
    const disabled = action === 'carry' && !carryTargetShift;
    return (
      <button
        onClick={() => !disabled && setDecision(allocId, action)}
        disabled={disabled}
        style={{
          padding: '7px 4px', fontSize: 11, fontWeight: 500, borderRadius: 6,
          border: '1px solid ' + (isSelected ? selectedStyle.borderColor : 'var(--border-default)'),
          background: isSelected ? selectedStyle.bg : 'var(--surface-inset)',
          color: isSelected ? selectedStyle.color : disabled ? 'var(--text-subtle)' : 'var(--text-muted)',
          cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Header */}
      <div style={{ padding: '8px 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface-app)', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '50%', color: 'var(--text-muted)', cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-default)' }}>Close shift</div>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>Shift {shift.shiftNumber} · reconcile {openAllocs.length} open</div>
        </div>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {/* Warning */}
        <div style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid #fbbf24', borderRadius: 10, padding: '12px 14px', fontSize: 12, color: '#fbbf24', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 9v4M12 17h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
          <div>
            <strong style={{ color: 'var(--text-default)', display: 'block', marginBottom: 2 }}>{openAllocs.length} devices still out</strong>
            Before closing, decide what happens to each.
          </div>
        </div>

        {/* Reconciliation rows */}
        {openAllocs.map(alloc => {
          const device = (st.devices || []).find(d => d.id === alloc.deviceId);
          return (
            <div key={alloc.id} style={{ background: 'var(--surface-default)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-default)', fontVariantNumeric: 'tabular-nums', fontFamily: alloc.anonDeviceLabel ? 'monospace' : 'inherit' }}>{alloc.anonDeviceLabel || device?.serial || alloc.deviceId}</span>
                  <span style={{ marginLeft: 6, padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', background: 'rgba(251,191,36,0.12)', color: '#fbbf24' }}>{device?.type || ''}</span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Out {fmtDuration(alloc.outAt)}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                {alloc.role} · {alloc.operativeName || 'Unassigned'}{alloc.operativePhone ? ` · ${alloc.operativePhone}` : ''}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                {actionBtn(alloc.id, 'return', 'Returned', { bg: 'rgba(74,222,128,0.12)', borderColor: '#4ade80', color: '#4ade80' })}
                {actionBtn(alloc.id, 'carry', 'Carry over', { bg: 'rgba(96,165,250,0.12)', borderColor: '#60a5fa', color: '#60a5fa' })}
                {actionBtn(alloc.id, 'flag', 'Flag', { bg: 'rgba(248,113,113,0.12)', borderColor: '#f87171', color: '#f87171' })}
              </div>
            </div>
          );
        })}

        {/* Summary */}
        <div style={{ background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 10, padding: 14, marginTop: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)' }}>Marked returned</span>
            <span style={{ color: 'var(--text-default)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{counts.return}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)' }}>Carrying to {carryTargetShift ? 'Shift ' + carryTargetShift.shiftNumber : 'next shift'}</span>
            <span style={{ color: 'var(--text-default)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{counts.carry}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)' }}>Flagged as still on site</span>
            <span style={{ color: counts.flag > 0 ? '#f87171' : 'var(--text-default)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{counts.flag}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--border-subtle)', marginTop: 6, fontSize: 12 }}>
            <span style={{ color: 'var(--text-default)', fontWeight: 600 }}>Shift will close as</span>
            <span style={{ color: counts.flag > 0 ? '#fbbf24' : 'var(--text-default)', fontWeight: 600 }}>
              {counts.flag > 0 ? 'Closed with open items' : 'Closed'}
            </span>
          </div>
        </div>

        <button onClick={handleConfirm} style={{ width: '100%', padding: 13, borderRadius: 10, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#ff530d', color: '#fff' }}>
          Confirm & close shift
        </button>
        <button onClick={onBack} style={{ width: '100%', padding: 13, marginTop: 8, borderRadius: 10, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 500, cursor: 'pointer', background: 'transparent', color: 'var(--text-muted)' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
